import { AdobeDTMWindow } from '@allianz/tracking/models/trackify.model';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface window extends AdobeDTMWindow {
  satelliteLoaded: boolean;
  trackEvent: 'pending' | 'emitted';
}

declare const document: Document;
declare const window: window;

export const trackingInitializer = () => () => {
  const trackingPathMapping = {
    'spaData..process.attributes.processDetails.businessEvent': {
      forwardTo: 'process.attributes.processDetails.businessEvent',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.repairShop': {
      forwardTo: 'process.attributes.processDetails.repairShop',
      forwardTrigger: true,
    },
    'spaData..user.type': { forwardTo: 'user.type', forwardTrigger: true },
    'spaData..user.consent': { forwardTo: 'user.consent', forwardTrigger: true },
    'spaData..user.profileID': { forwardTo: 'user.profileID', forwardTrigger: true },
    'spaData..user.loginStatus': { forwardTo: 'user.loginStatus', forwardTrigger: true },
    'spaData..user.details': { forwardTo: 'user.details', forwardTrigger: true },
    'spaData..process.category': { forwardTo: 'process.category', forwardTrigger: true },
    'spaData..process.processName': { forwardTo: 'process.processName', forwardTrigger: true },
    'spaData..process.stepName': { forwardTo: 'process.stepName', forwardTrigger: true },
    'spaData..process.stepNumber': { forwardTo: 'process.stepNumber', forwardTrigger: true },
    'spaData..process.stepBetweenName': { forwardTo: 'process.stepBetweenName', forwardTrigger: true },
    'spaData..helptext.navigationButton': { forwardTo: 'helptext.navigationButton', forwardTrigger: true },
    'spaData..page.attributes.popup.text': { forwardTo: 'page.attributes.popup.text', forwardTrigger: true },
    'spaData..page.error': { forwardTo: 'page.error', forwardTrigger: true },
    'spaData..page.pageInfo.pageName': { forwardTo: 'page.pageInfo.pageName', forwardTrigger: true },
    'spaData..page.pageInfo.language': { forwardTo: 'page.pageInfo.language', forwardTrigger: true },
    'spaData..page.pageInfo.siteSection': { forwardTo: 'page.pageInfo.siteSection', forwardTrigger: true },
    'spaData..page.product.name': { forwardTo: 'product.name', forwardTrigger: true },
    'spaData..page.product.price': { forwardTo: 'product.price', forwardTrigger: true },
    'spaData..page.product.paymentFrequency': { forwardTo: 'product.paymentFrequency', forwardTrigger: true },
    'spaData..page.product.paymentMethod': { forwardTo: 'product.paymentMethod', forwardTrigger: true },
    'spaData..page.product.policyNumber': { forwardTo: 'product.policyNumber', forwardTrigger: true },
    'spaData..page.error.type': { forwardTo: 'page.error.type', forwardTrigger: true },
    'spaData..page.error.code': { forwardTo: 'page.error.code', forwardTrigger: true },
    'spaData..page.error.message': { forwardTo: 'page.error.message', forwardTrigger: true },
    'spaData..process.attributes.processDetails.travelDetails.insuranceSubject': {
      forwardTo: 'process.attributes.processDetails.travelDetails.insuranceSubject',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.destination': {
      forwardTo: 'process.attributes.processDetails.travelDetails.destination',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.departureDate': {
      forwardTo: 'process.attributes.processDetails.travelDetails.departureDate',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.returnDate': {
      forwardTo: 'process.attributes.processDetails.travelDetails.returnDate',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.peopleNumber': {
      forwardTo: 'process.attributes.processDetails.travelDetails.peopleNumber',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.allInPoland': {
      forwardTo: 'process.attributes.processDetails.travelDetails.allInPoland',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.extremeSports': {
      forwardTo: 'process.attributes.processDetails.travelDetails.extremeSports',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.phoneNumber': {
      forwardTo: 'process.attributes.processDetails.travelDetails.phoneNumber',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.travelersDOB': {
      forwardTo: 'process.attributes.processDetails.travelDetails.travelersDOB',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.extremeSportsEnabled': {
      forwardTo: 'process.attributes.processDetails.travelDetails.extremeSportsEnabled',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.extension': {
      forwardTo: 'process.attributes.processDetails.travelDetails.extension',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.promoCode': {
      forwardTo: 'process.attributes.processDetails.travelDetails.promoCode',
      forwardTrigger: true,
    },
    'spaData..process.attributes.processDetails.travelDetails.specialOffer': {
      forwardTo: 'process.attributes.processDetails.travelDetails.specialOffer',
      forwardTrigger: true,
    },
    'spaData..product.productInfo.primaryCategory': { forwardTo: 'product.productInfo.primaryCategory', forwardTrigger: true },
    'spaData..product.name': { forwardTo: 'product.name', forwardTrigger: true },
    'spaData..product.price': { forwardTo: 'product.price', forwardTrigger: true },
    'spaData..product.paymentFrequency': { forwardTo: 'product.paymentFrequency', forwardTrigger: true },
    'spaData..page.attributes.popup': { forwardTo: 'page.attributes.popup', forwardTrigger: true },
    'spaData..process.previousStepName': { forwardTo: 'process.previousStepName', forwardTrigger: true },
    'spaData..process.previousStepNumber': { forwardTo: 'process.previousStepNumber', forwardTrigger: true },
    'spaData..version': { forwardTo: 'version', forwardTrigger: true },
  };
  if (!window.trackifyDefines) {
    // be safe to not override custom settings done by a tracking expert
    window.trackifyDefines = trackingPathMapping;
  } else {
    // merge with custom settings done by a tracking expert
    // using this order, Angular custom mapping potentially overrides an existing rewrite for an event name setup in Launch
    // window.trackifyDefines = Object.assign(window.trackifyDefines, trackingPathMapping);
    // if we want the Launch custom mappings to be taken with higher relevance:
    window.trackifyDefines = Object.assign(trackingPathMapping, window.trackifyDefines);
  }
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  setReferrerIfNone();
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  overrideSatellite();
};

const overrideSatellite = (): void => {
  /* eslint-disable */
  const unsubscribe = new Subject<void>();
  interval(500).pipe(takeUntil(unsubscribe))
    .subscribe(() => {
      if(window._satellite) {
        window._satellite.logger.log('Overriding Track');
        window._satellite._trackedEvents = window._satellite._trackedEvents || [];

        window.satelliteLoaded = true;
        const eSatelliteCreated = new CustomEvent('adobeSatelliteLoaded');
        window.dispatchEvent(eSatelliteCreated);

        const t = window._satellite.track;
        window._satellite.track = (myEvent: any) => {
          const localCopy = JSON.parse(JSON.stringify(window.digitalData));
          window._satellite._trackedEvents.push({ event: myEvent, data: localCopy });

          window['trackEvent'] = 'pending';

          setTimeout(() => {
            if (typeof window['trackEvent'] === 'undefined' || window['trackEvent'] === 'pending') {
              const eTrackTimeout = new CustomEvent('adobeTrackingDone', { detail: { status: 'timeout' } });
              window['trackEvent'] = 'emitted';
              window.dispatchEvent(eTrackTimeout);
            }
          }, 500);

          t(myEvent, localCopy);
        };
        unsubscribe.next();
        unsubscribe.complete();
      }
    });
};

const setReferrerIfNone = (): void => {
  if (!sessionStorage.getItem('lastReferrer')) {
    sessionStorage.setItem('lastReferrer', document.referrer);
  }
};
